import { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
    ChevronLeft, 
    ChevronRight, 
    Maximize2, 
    Minimize2,
    Monitor,
    Play,
    Pause,
    X
} from 'lucide-react';

// Slide style themes
const THEMES = {
    professional: {
        bgGradient: 'from-blue-600 via-blue-700 to-indigo-800',
        titleColor: 'text-white',
        textColor: 'text-blue-100',
        accentColor: 'bg-blue-400',
        bulletColor: 'bg-white'
    },
    modern: {
        bgGradient: 'from-violet-600 via-purple-700 to-fuchsia-800',
        titleColor: 'text-white',
        textColor: 'text-purple-100',
        accentColor: 'bg-purple-400',
        bulletColor: 'bg-white'
    },
    corporate: {
        bgGradient: 'from-slate-700 via-slate-800 to-gray-900',
        titleColor: 'text-white',
        textColor: 'text-slate-200',
        accentColor: 'bg-slate-400',
        bulletColor: 'bg-white'
    },
    creative: {
        bgGradient: 'from-pink-500 via-rose-600 to-orange-600',
        titleColor: 'text-white',
        textColor: 'text-pink-100',
        accentColor: 'bg-rose-300',
        bulletColor: 'bg-white'
    },
    dark: {
        bgGradient: 'from-gray-900 via-black to-gray-900',
        titleColor: 'text-white',
        textColor: 'text-gray-300',
        accentColor: 'bg-blue-500',
        bulletColor: 'bg-blue-400'
    },
    light: {
        bgGradient: 'from-gray-50 via-white to-gray-100',
        titleColor: 'text-gray-900',
        textColor: 'text-gray-700',
        accentColor: 'bg-blue-500',
        bulletColor: 'bg-blue-500'
    }
};

const SlideViewer = ({ 
    slides = [], 
    title = 'Presentation',
    theme = 'professional',
    open = false,
    onOpenChange,
    autoPlay = false,
    autoPlayInterval = 5000
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    
    const themeStyles = THEMES[theme] || THEMES.professional;

    // Auto-play functionality
    useEffect(() => {
        if (!isPlaying || !open) return;
        
        const timer = setInterval(() => {
            setCurrentSlide(prev => {
                if (prev >= slides.length - 1) {
                    setIsPlaying(false);
                    return prev;
                }
                return prev + 1;
            });
        }, autoPlayInterval);
        
        return () => clearInterval(timer);
    }, [isPlaying, open, slides.length, autoPlayInterval]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (!open) return;
        
        if (e.key === 'ArrowRight' || e.key === ' ') {
            e.preventDefault();
            setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1));
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setCurrentSlide(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
            if (isFullscreen) {
                setIsFullscreen(false);
            } else {
                onOpenChange?.(false);
            }
        } else if (e.key === 'f' || e.key === 'F') {
            setIsFullscreen(prev => !prev);
        }
    }, [open, slides.length, isFullscreen, onOpenChange]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Reset slide when opening
    useEffect(() => {
        if (open) {
            setCurrentSlide(0);
        }
    }, [open]);

    const renderSlide = (slide, index) => {
        if (!slide) return null;
        
        const isTitle = slide.type === 'title';
        const isObjectives = slide.type === 'objectives';
        
        return (
            <div 
                className={`w-full h-full bg-gradient-to-br ${themeStyles.bgGradient} rounded-xl overflow-hidden shadow-2xl relative`}
                style={{ aspectRatio: '16/9', minHeight: isFullscreen ? 'calc(100vh - 150px)' : '400px' }}
            >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
                
                {/* Slide number badge */}
                <div className="absolute top-4 right-4">
                    <Badge variant="secondary" className="bg-white/20 text-white border-0">
                        {index + 1} / {slides.length}
                    </Badge>
                </div>
                
                {/* Content */}
                <div className="relative z-10 h-full flex flex-col p-8 md:p-12">
                    {isTitle ? (
                        // Title slide
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                            <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold mb-6 ${themeStyles.titleColor} drop-shadow-lg`}>
                                {slide.title}
                            </h1>
                            {slide.subtitle && (
                                <p className={`text-xl md:text-2xl lg:text-3xl ${themeStyles.textColor} font-light`}>
                                    {slide.subtitle}
                                </p>
                            )}
                        </div>
                    ) : (
                        // Content slide
                        <>
                            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-bold mb-8 ${themeStyles.titleColor} relative inline-block`}>
                                {slide.title}
                                <div className={`absolute -bottom-2 left-0 w-24 h-1 ${themeStyles.accentColor} rounded-full`} />
                            </h2>
                            
                            <div className="flex-1 flex flex-col justify-center">
                                {slide.bullet_points && slide.bullet_points.length > 0 ? (
                                    <ul className="space-y-4 md:space-y-6">
                                        {slide.bullet_points.map((point, idx) => (
                                            <li 
                                                key={idx}
                                                className={`flex items-start gap-4 ${themeStyles.textColor} text-lg md:text-xl lg:text-2xl`}
                                                style={{
                                                    animation: `slideIn 0.5s ease-out ${idx * 0.1}s both`
                                                }}
                                            >
                                                <span className={`w-3 h-3 ${themeStyles.bulletColor} rounded-full mt-2 flex-shrink-0 shadow-lg`} />
                                                <span className="leading-relaxed">{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : slide.content ? (
                                    <p className={`text-lg md:text-xl lg:text-2xl ${themeStyles.textColor} leading-relaxed`}>
                                        {slide.content}
                                    </p>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
                
                {/* Animation styles */}
                <style jsx>{`
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                `}</style>
            </div>
        );
    };

    if (!slides || slides.length === 0) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent 
                className={`${
                    isFullscreen 
                        ? 'max-w-[100vw] w-screen h-screen m-0 rounded-none p-4' 
                        : 'max-w-6xl w-[95vw]'
                } bg-gray-950`}
            >
                <DialogHeader className="pb-4">
                    <DialogTitle className="flex items-center justify-between text-white">
                        <span className="flex items-center gap-3">
                            <Monitor className="h-5 w-5 text-blue-400" />
                            <span className="truncate max-w-[300px]">{title}</span>
                        </span>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setIsPlaying(!isPlaying)}
                                className="text-white hover:bg-white/10"
                            >
                                {isPlaying ? (
                                    <Pause className="h-4 w-4" />
                                ) : (
                                    <Play className="h-4 w-4" />
                                )}
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="text-white hover:bg-white/10"
                            >
                                {isFullscreen ? (
                                    <Minimize2 className="h-4 w-4" />
                                ) : (
                                    <Maximize2 className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                    {/* Main slide display */}
                    <div className="relative">
                        {renderSlide(slides[currentSlide], currentSlide)}
                        
                        {/* Navigation arrows on slide */}
                        <button
                            onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                            disabled={currentSlide === 0}
                            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/30 hover:bg-black/50 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                            onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                            disabled={currentSlide === slides.length - 1}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/30 hover:bg-black/50 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    </div>
                    
                    {/* Bottom controls */}
                    <div className="flex items-center justify-between px-2">
                        <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                            disabled={currentSlide === 0}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                        </Button>
                        
                        {/* Slide indicators */}
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400">
                                {currentSlide + 1} of {slides.length}
                            </span>
                            <div className="flex gap-1.5">
                                {slides.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentSlide(idx)}
                                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                                            idx === currentSlide 
                                                ? 'bg-blue-500 scale-125' 
                                                : 'bg-gray-600 hover:bg-gray-500'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                            disabled={currentSlide === slides.length - 1}
                            className="bg-transparent border-white/20 text-white hover:bg-white/10"
                        >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                    
                    {/* Keyboard hints */}
                    <div className="flex justify-center gap-4 text-xs text-gray-500">
                        <span>← → Navigate</span>
                        <span>Space Next</span>
                        <span>F Fullscreen</span>
                        <span>Esc Close</span>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SlideViewer;
